// @ts-strict-ignore
/** @jsx jsx */
/** @jsxFrag null */
import ResizeObserver from 'resize-observer-polyfill';
import {VNode} from 'snabbdom';
import {Component, jsx} from 'panel';

import {ChartsContextRegistry} from 'common/charts/contexts/registry';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {SegmentFilterController} from 'common/charts/contexts/segment-filter-controller';
import {LabelTheme, Section, TooltipInfo} from 'common/components/items-menu/types';
import {ChartLegendEl} from 'common/components/chart-legend/panel';
import {ChartLegendTheme} from 'common/components/chart-legend/constants';
import {Placement} from 'common/components/tooltip/types';
import {panel2react} from 'common/util/react';

import {SegmentationLimitInfoEl} from '../segmentation-limit-info';

interface SegmentFilterState {
  selfHeight: string;
}

interface LegendSectionItemValue {
  sectionIdx: number;
  segmentId: string;
}

/**
 * Wrapper component for a chart legend used only for bar charts
 */
export class SegmentFilter extends Component<SegmentFilterState, EmptyObj, any, any, ChartsContextRegistry> {
  get config() {
    return {
      template: () => this.renderTemplate(),
      defaultState: {
        selfHeight: `0px`,
      },
      contexts: [`segmentFilterController` as const],
    };
  }

  private declare selfResizeObserver: ResizeObserver;

  private get segmentFilterController(): SegmentFilterController {
    return this.getContext(`segmentFilterController`);
  }

  connectedCallback() {
    this.selfResizeObserver = new ResizeObserver((entries) => {
      const {height} = entries[0].contentRect;
      this.update({
        selfHeight: `${height}px`,
      });
    });

    this.selfResizeObserver.observe(this);

    super.connectedCallback();
  }

  disconnectedCallback() {
    this.selfResizeObserver?.disconnect();
    super.disconnectedCallback();
  }

  private renderTemplate(): VNode {
    const maxSelectedTooltip: TooltipInfo = {
      text: `Cannot select more than ${this.segmentFilterController.maxNumSelections} segments.`,
      placement: Placement.Left,
    };

    const legendSections: Array<Section<LegendSectionItemValue>> = this.segmentFilterController
      .getSections()
      .map((section, sectionIdx) => {
        const numSelectedLabel =
          section.apiResultsTruncated && !section.suppressShowingTop
            ? `${this.segmentFilterController.getSelectedCount(sectionIdx)} of top ${section.items.length}`
            : `${this.segmentFilterController.getSelectedCount(sectionIdx)} of ${section.items.length}`;
        const sectionLabel = section.label ? `${section.label} ` : ``;

        const sectionLabelIconRenderer = section.apiResultsTruncated
          ? () => {
              return (
                <SegmentationLimitInfoEl
                  attrs={{
                    'segmentation-limit': this.segmentFilterController.segmentationQueryLimit,
                    'export-segmentation-limit': this.segmentFilterController.exportSegmentationQueryLimit,
                  }}
                />
                // expecting 'export-csv' event to bubble from SegmentationLimitInfo, handled by an ancestor
              );
            }
          : null;

        return {
          label: `${sectionLabel}${numSelectedLabel}`,
          showLimit: 12,
          items: section.items.map((segmentItem) => {
            const isSelected = this.segmentFilterController.isItemSelected(sectionIdx, segmentItem.id);

            // can only hit max selections on first section
            const isDisabled = sectionIdx === 0 && !isSelected && this.segmentFilterController.isMaxSelected();
            const tooltip = isDisabled ? maxSelectedTooltip : null;

            return {
              label: segmentItem.label,
              value: {segmentId: segmentItem.id, sectionIdx},
              isSelected,
              isDisabled,
              tooltip,
              color: this.segmentFilterController.getItemColor(sectionIdx, segmentItem.id),
              labelTheme: LabelTheme.Soft,
              wrapLabel: true,
            };
          }),
          sectionLabelIconRenderer,
        };
      });

    const moreItemsThanLimit = legendSections[0]?.items.length > this.segmentFilterController.maxNumSelections;

    return (
      <div sel='.root'>
        <ChartLegendEl
          sel='.large-legend'
          loading={!this.segmentFilterController.hasLoaded()}
          height={this.state.selfHeight}
          theme={ChartLegendTheme.Primary}
          showSelectAll
          selectAllTooltip={moreItemsThanLimit ? maxSelectedTooltip : undefined}
          enableSearch
          maxLegendItems={12}
          sections={legendSections}
          onToggleCheckbox={(toggle) => {
            if (toggle.isSelectAll) {
              this.segmentFilterController.toggleAllItems();
            } else if (toggle.item) {
              this.segmentFilterController.toggleItem(toggle.item.value.sectionIdx, toggle.item.value.segmentId);
            }
          }}
        />
      </div>
    );
  }
}

export const SegmentFilterEl = defineAndMakeJsxFactory(`mp-segment-filter`, SegmentFilter);
export const SegmentFilterReact = panel2react(`mp-segment-filter`, SegmentFilter);
